import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

const AVAILABLE_ATTRIBUTE = "data-connection-recovery-available"
const ACTIVE_ATTRIBUTE = "data-connection-recovery-active"
const GENERATION_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const ATTEMPT_ID_PATTERN = /^[1-9][0-9]*-[1-9][0-9]*$/

function generateConnectionGeneration() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID()

  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"))
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`
}

export default class extends BridgeComponent {
  static component = "connection-recovery"

  connect() {
    super.connect()
    this.failedFrames ||= new Set()
    this.retryBatchInFlight ||= false
    this.nativeRecoveryActive = false
    this.connectionGeneration = generateConnectionGeneration()
    this.root.setAttribute(AVAILABLE_ATTRIBUTE, "")

    const generation = this.connectionGeneration
    this.send("connect", { generation }, (message) => this.receive(message, generation))
    this.reportFailure()
  }

  disconnect() {
    const generation = this.connectionGeneration
    this.connectionGeneration = null
    this.retryBatchInFlight = false
    this.retryBatchGeneration = null
    this.nativeRecoveryActive = false
    this.failedFrames?.clear()
    this.root.removeAttribute(ACTIVE_ATTRIBUTE)
    this.root.removeAttribute(AVAILABLE_ATTRIBUTE)
    super.disconnect()

    if (this.validGeneration(generation)) this.send("disconnect", { generation })
  }

  frameMissing(event) {
    this.recordFailure(event)
  }

  fetchError(event) {
    this.recordFailure(event)
  }

  frameLoad(event) {
    const frame = this.frameFor(event.target)
    if (!frame || !this.failedFrames?.has(frame)) return

    this.failedFrames.delete(frame)
    this.updateActiveMarker()
  }

  recordFailure(event) {
    const frame = this.frameFor(event.target)
    if (!frame || !this.validGeneration(this.connectionGeneration)) return

    event.preventDefault()
    this.failedFrames.add(frame)
    this.updateActiveMarker()
    if (!this.retryBatchInFlight) this.reportFailure()
  }

  receive(message, expectedGeneration) {
    const generation = message?.data?.generation
    if (
      expectedGeneration !== this.connectionGeneration ||
      generation !== this.connectionGeneration
    ) return

    if (message?.data?.action === "state") {
      if (typeof message.data.active !== "boolean") return

      this.nativeRecoveryActive = message.data.active
      this.updateActiveMarker()
      return
    }

    const attemptId = message?.data?.attemptId
    if (
      !this.validAttemptId(attemptId)
    ) return

    if (this.retryBatchInFlight) {
      this.sendOutcome("unavailable", generation, attemptId)
      return
    }

    this.pruneFailedFrames()
    const batch = [...this.failedFrames]
    if (batch.length === 0) {
      this.sendOutcome("recovered", generation, attemptId)
      return
    }

    this.retryBatchInFlight = true
    this.retryBatchGeneration = generation
    Promise.allSettled(batch.map((frame) => this.reloadFrame(frame))).then(() => {
      if (this.retryBatchGeneration === generation) {
        this.retryBatchInFlight = false
        this.retryBatchGeneration = null
      }
      if (generation !== this.connectionGeneration) return

      this.pruneFailedFrames()
      const outcome = this.failedFrames.size === 0 ? "recovered" : "attempt-failed"
      this.sendOutcome(outcome, generation, attemptId)
    })
  }

  reloadFrame(frame) {
    try {
      if (frame.hasAttribute("src")) {
        frame.reload()
        return Promise.resolve(frame.loaded)
      }

      const source = frame.dataset.src
      if (!source) return Promise.resolve()

      frame.loading = "eager"
      frame.src = source
      return Promise.resolve(frame.loaded)
    } catch (_error) {
      return Promise.resolve()
    }
  }

  reportFailure() {
    if (!this.validGeneration(this.connectionGeneration) || this.failedFrames.size === 0) return

    this.send("failed", {
      generation: this.connectionGeneration,
      frameIds: this.failedFrameIds()
    })
  }

  sendOutcome(event, generation, attemptId) {
    if (generation !== this.connectionGeneration) return

    this.updateActiveMarker()
    this.send(event, {
      generation,
      attemptId,
      frameIds: this.failedFrameIds()
    })
  }

  pruneFailedFrames() {
    for (const frame of this.failedFrames) {
      if (!frame.isConnected) this.failedFrames.delete(frame)
    }
    this.updateActiveMarker()
  }

  updateActiveMarker() {
    this.root.toggleAttribute(
      ACTIVE_ATTRIBUTE,
      this.nativeRecoveryActive || this.failedFrames?.size > 0
    )
  }

  failedFrameIds() {
    return [...this.failedFrames]
      .map((frame) => frame.id)
      .filter((id) => id.length > 0)
      .sort()
  }

  frameFor(target) {
    if (!(target instanceof Element)) return null
    return target.matches("turbo-frame") ? target : null
  }

  validGeneration(generation) {
    return typeof generation === "string" && GENERATION_PATTERN.test(generation)
  }

  validAttemptId(attemptId) {
    return typeof attemptId === "string" && ATTEMPT_ID_PATTERN.test(attemptId)
  }

  get root() {
    return document.documentElement
  }
}
